import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import CommentFormatToolbar from './CommentFormatToolbar';
import { handleListEnter } from './commentMarkdown';
import { usePasteImage, PendingPaste } from 'src/hooks/usePasteImage';

type Installation = {
  id: number;
  name: string;
  product: number;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  // When opened from an installation's Tickets tab, lock the picker to it.
  defaultInstallationId?: number;
}

function PartnerCreateTicketModal({
  open,
  onClose,
  onCreated,
  defaultInstallationId
}: Props) {
  const intl = useIntl();
  const [subject, setSubject] = useState('');
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [selectedInstallation, setSelectedInstallation] =
    useState<Installation | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const [pendingPastes, setPendingPastes] = useState<PendingPaste[]>([]);
  const [pasteError, setPasteError] = useState<'tooLarge' | 'uploadFailed' | null>(
    null
  );

  const pendingPastesRef = useRef<PendingPaste[]>([]);
  pendingPastesRef.current = pendingPastes;

  useEffect(() => {
    return () => {
      pendingPastesRef.current.forEach((p) =>
        window.URL.revokeObjectURL(p.blobUrl)
      );
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    // Start each open with a clean form so a cancelled draft (and its pasted
    // blob URLs) never bleeds into the next open / a different installation.
    setSubject('');
    setDescription('');
    setSelectedFiles([]);
    setError('');
    setSelectedInstallation(null);
    setPendingPastes((prev) => {
      prev.forEach((p) => window.URL.revokeObjectURL(p.blobUrl));
      return [];
    });
    axiosConfig
      .get('/GetMyInstallations')
      .then((res) => {
        setInstallations(res.data);
        if (defaultInstallationId != null) {
          const match = (res.data as Installation[]).find(
            (i) => i.id === defaultInstallationId
          );
          if (match) setSelectedInstallation(match);
        }
      })
      .catch(() =>
        setError(
          intl.formatMessage({
            id: 'failedToLoadInstallations',
            defaultMessage: 'Failed to load installations.'
          })
        )
      );
  }, [open, intl, defaultInstallationId]);

  const handlePasteDescription = usePasteImage({
    mode: 'deferred',
    textareaRef: descriptionRef,
    value: description,
    onChange: setDescription,
    onError: setPasteError,
    onBufferedPaste: (p) => setPendingPastes((prev) => [...prev, p])
  });

  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf'
  ];
  const MAX_FILE_SIZE = 25 * 1024 * 1024;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(
          intl.formatMessage({
            id: 'invalidFileType',
            defaultMessage: 'Invalid file type.'
          })
        );
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(
          intl.formatMessage({
            id: 'fileTooLarge',
            defaultMessage: 'File exceeds maximum size of 25 MB.'
          })
        );
        return;
      }
    }
    setError('');
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setSubject('');
    setSelectedInstallation(null);
    setDescription('');
    setSelectedFiles([]);
    setPendingPastes([]);
    setError('');
  };

  // Upload buffered pasted images, then rewrite their blob: URLs in the
  // description to the stored /DownloadDocument URLs.
  const uploadPastedImages = async (newTicketId: number) => {
    let finalDescription = description;
    let descChanged = false;
    for (const paste of pendingPastes) {
      const formData = new FormData();
      formData.append('file', paste.blob);
      try {
        const upRes = await axiosConfig.post('/UploadDocument', formData, {
          params: { scope: 0, ticketId: newTicketId },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const docId = upRes.data?.id;
        if (docId && finalDescription.includes(paste.blobUrl)) {
          finalDescription = finalDescription
            .split(paste.blobUrl)
            .join(`/DownloadDocument?id=${docId}`);
          descChanged = true;
        }
      } catch (err: any) {
        console.warn('[Paste] Upload failed:', err?.response?.data || err?.message);
        setPasteError('uploadFailed');
      }
      window.URL.revokeObjectURL(paste.blobUrl);
    }
    if (descChanged) {
      try {
        await axiosConfig.put('/UpdateMyTicket', {
          id: newTicketId,
          subject,
          description: finalDescription
        });
      } catch (err: any) {
        console.warn('[Paste] Description rewrite failed:', err?.response?.data || err?.message);
      }
    }
  };

  const uploadAttachments = async (newTicketId: number) => {
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axiosConfig.post('/UploadDocument', formData, {
          params: { scope: 0, ticketId: newTicketId },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err: any) {
        console.warn(
          `[Documents] Upload failed for ${file.name}:`,
          err?.response?.data || err?.message
        );
      }
    }
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      setError(
        intl.formatMessage({
          id: 'subjectRequired',
          defaultMessage: 'Subject is required.'
        })
      );
      return;
    }
    // Prefer the picked installation; fall back to the locked default from the
    // tab so a slow/failed GetMyInstallations fetch can't dead-end the form
    // (the backend re-checks access on the id anyway).
    const installationId = selectedInstallation?.id ?? defaultInstallationId;
    if (installationId == null) {
      setError(
        intl.formatMessage({
          id: 'installationRequired',
          defaultMessage: 'Please select an installation.'
        })
      );
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await axiosConfig.post('/CreatePartnerTicket', {
        subject,
        description,
        installationId
      });

      const newTicketId = res.data?.id;
      if (newTicketId) {
        setUploading(true);
        if (pendingPastes.length > 0) await uploadPastedImages(newTicketId);
        if (selectedFiles.length > 0) await uploadAttachments(newTicketId);
        setUploading(false);
      }

      resetForm();
      onCreated();
      onClose();
    } catch {
      setError(
        intl.formatMessage({
          id: 'createTicketFailed',
          defaultMessage: 'Failed to create ticket.'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="createTicket" defaultMessage="Create Ticket" />
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label={<FormattedMessage id="subject" defaultMessage="Subject" />}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          fullWidth
          margin="dense"
        />

        <Autocomplete<Installation, false, false, false>
          options={installations}
          getOptionLabel={(o) => o.name}
          value={selectedInstallation}
          onChange={(_, v) => setSelectedInstallation(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disabled={defaultInstallationId != null}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              margin="dense"
              label={
                <FormattedMessage
                  id="installation"
                  defaultMessage="Installation"
                />
              }
            />
          )}
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            <FormattedMessage id="description" defaultMessage="Description" />
          </Typography>
          <CommentFormatToolbar
            textareaRef={descriptionRef}
            value={description}
            onChange={setDescription}
            disabled={submitting || uploading}
          />
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => handleListEnter(e, description, setDescription)}
            onPaste={handlePasteDescription}
            inputRef={descriptionRef}
            fullWidth
            multiline
            minRows={4}
            margin="dense"
          />
        </Box>

        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting || uploading}
          >
            <FormattedMessage id="attachFiles" defaultMessage="Attach files" />
          </Button>
          {selectedFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {selectedFiles.map((f, i) => (
                <Chip
                  key={i}
                  label={f.name}
                  size="small"
                  onDelete={() => removeFile(i)}
                />
              ))}
            </Box>
          )}
        </Box>

        {uploading && <LinearProgress />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || uploading}>
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || uploading}
        >
          <FormattedMessage id="createTicket" defaultMessage="Create Ticket" />
        </Button>
      </DialogActions>

      <Snackbar
        open={pasteError !== null}
        autoHideDuration={4000}
        onClose={() => setPasteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setPasteError(null)}>
          {pasteError === 'tooLarge' ? (
            <FormattedMessage
              id="pasteImageTooLarge"
              defaultMessage="Pasted image exceeds 100 MB limit"
            />
          ) : (
            <FormattedMessage
              id="pasteImageUploadFailed"
              defaultMessage="Failed to upload pasted image"
            />
          )}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default PartnerCreateTicketModal;
